import { useEffect, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { createPath, type DataRouter } from "react-router";
import { loadProcessTerms } from "../api/content/processTermWords";
import ErrorNotice from "../errors/ErrorNotice";
import { useSession } from "../session/sessionContext";
import { sessionAllowsAppAccess } from "../session/sessionMachine";
import LoadingIndicator from "../ui/feedback/LoadingIndicator";
import { appPaths, type ReturnAfterSignIn } from "../shared/routing/appPaths";

interface RequireSessionProps {
  /** The window's router: without a session it goes to sign in, and back here after. */
  readonly router: DataRouter;
  /** The working app, given the signed-in account's ID. */
  readonly children: (accountId: string) => ReactNode;
}

/** Opens the working app only with a session; otherwise sends the user to sign in, and back here after. */
export default function RequireSession({ router, children }: RequireSessionProps) {
  const { t } = useTranslation();
  const { session, retrySessionCheck } = useSession();
  const allowed = session.status !== "checking" && session.status !== "checkFailed" && sessionAllowsAppAccess(session);
  const signedOut = session.status !== "checking" && session.status !== "checkFailed" && !allowed;
  // The ontology's words for its terms, which every view names types and kinds by (V23).
  useEffect(() => {
    if (allowed) void loadProcessTerms();
  }, [allowed]);
  useEffect(() => {
    if (!signedOut) return;
    const state: ReturnAfterSignIn = { returnTo: createPath(router.state.location) };
    void router.navigate(appPaths.login(), { replace: true, state });
  }, [signedOut, router]);

  if (session.status === "checking") {
    return (
      <div className="app-main flex h-screen items-center justify-center">
        <LoadingIndicator label={t("session.checking")} />
      </div>
    );
  }
  if (session.status === "checkFailed") {
    return (
      <div className="app-main flex h-screen items-center justify-center p-6">
        <ErrorNotice error={session.error} onRetry={retrySessionCheck} className="w-full max-w-md" />
      </div>
    );
  }
  return session.status === "signedIn" && allowed ? children(session.session.account.userId) : null;
}
